import React from 'react';

interface ToggleSwitchProps {
    isChecked: boolean;
    onChange: (checked: boolean) => void;
    labelOn?: string;
    labelOff?: string;
    colorOn?: string;
    colorOff?: string;
    appTheme?: string;
}

export const ToggleSwitch: React.FC<ToggleSwitchProps> = ({
    isChecked,
    onChange,
    labelOn = "On",
    labelOff = "Off",
    appTheme = 'warm',
    colorOn = appTheme === 'dark' ? "rgb(69, 207, 207)" : appTheme === 'light' ? "rgb(0, 255, 255)" : "rgb(69, 207, 207)", // #e0e09f yellow
    colorOff = appTheme === 'dark' ? "rgb(120, 120, 120)" : appTheme === 'light' ? "rgb(215, 215, 215)" : "rgb(120, 120, 120)",
}) => {
    return (
        <label style={{ 
            display: 'flex',
            width: '15.3dvw',
            alignItems: 'center',
            textAlign: 'left',
            cursor: 'pointer',
            gap: '1dvw',
            margin: '0.7dvw',
            padding: '0.3dvw',
            outline: '0.04dvw solid hsl(45, 34.00%, 62.50%, 0.5)',
            borderRadius: '0.4dvw',
            backgroundColor: appTheme === 'dark' ? 'rgba(0, 0, 0, 0.7)' :
                             appTheme === 'light' ? 'rgba(124, 124, 124, 0.65)' :
                             'rgba(26, 26, 26, 0.8)', // warm theme
            // backgroundColor: 'rgba(255, 255, 255, 0.2)',
            WebkitTapHighlightColor: 'transparent'
        }}>
            <div style={{
                position: 'relative',
                width: '2dvw',
                height: '1.1dvw',
                outline: 'none',
                WebkitTapHighlightColor: 'transparent',
                transform: 'rotate(90deg)'
            }}>
                <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={(e) => onChange(e.target.checked)}
                    style={{
                        opacity: 0,
                        width: 0,
                        height: 0,
                        position: 'absolute',
                        outline: 'none'
                    }}
                />
                <span style={{
                    position: 'absolute',
                    cursor: 'pointer',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(255, 255, 255, 0.3)',
                    transition: 'all 0.4s ease',
                    borderRadius: '2dvw',
                    boxShadow: 'inset 0 0 0.3dvw rgba(0,0,0,0.5)',
                    outline: 'none',
                    WebkitTapHighlightColor: 'transparent'
                }}>
                    <div style={{
                        position: 'absolute',
                        height: '1dvw',
                        width: '1dvw',
                        left: isChecked ? '0.06dvw' : '0.96dvw',
                        bottom: '0.06dvw',
                        backgroundColor: colorOn,
                        transition: '0.4s',
                        borderRadius: '50%',
                        boxShadow: '0 0.1dvw 0.3dvw rgba(0,0,0,0.5)'
                    }} />
                </span>
            </div>
            <div style={{
                fontSize: '0.9dvw',
                display: 'flex',
                flexDirection: 'column',
                gap: '0.1dvw',
                outline: 'none',
                WebkitTapHighlightColor: 'transparent'
            }}>
                <span style={{
                    color: isChecked ? colorOn : colorOff,
                    fontWeight: isChecked ? '700' : '400',
                    outline: 'none',
                    WebkitTapHighlightColor: 'transparent'
                }}>
                    {labelOn}
                </span>
                <span style={{
                    color: !isChecked ? colorOn : colorOff,
                    fontWeight: !isChecked ? '700' : '400',
                    outline: 'none',
                    WebkitTapHighlightColor: 'transparent'
                }}>
                    {labelOff}
                </span>
            </div>
        </label>
    );
};

// Panel toggle indicator component
interface PanelToggleIndicatorProps {
    isShown: boolean;
    colorOn: string;
    colorOff: string;
}

export const PanelToggleIndicator: React.FC<PanelToggleIndicatorProps> = ({ isShown, colorOn, colorOff }) => (
    <div style={{
        position: 'relative',
        width: '2dvw',
        height: '1.1dvw',
        transform: 'rotate(90deg)'
    }}>
        <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.3)',
            borderRadius: '2dvw',
            boxShadow: 'inset 0 0 0.3dvw rgba(0,0,0,0.5)'
        }}>
            <div style={{
                position: 'absolute',
                height: '1dvw',
                width: '1dvw',
                left: isShown ? '0.06dvw' : '0.96dvw',
                bottom: '0.06dvw',
                backgroundColor: isShown ? colorOn : colorOff,
                transition: '0.4s',
                borderRadius: '50%',
                boxShadow: '0 0.1dvw 0.3dvw rgba(0,0,0,0.5)'
            }} />
        </div>
    </div>
);

// Slider component for numeric values
interface SliderProps {
    value: number;
    onChange: (value: number) => void;
    min: number;
    max: number;
    label: string;
    appTheme?: string;
}

export const Slider: React.FC<SliderProps> = ({
    value,
    onChange,
    min,
    max,
    label,
    appTheme = 'warm'
}) => {
    const colorOn = appTheme === 'dark' ? "rgb(69, 207, 207)" : appTheme === 'light' ? "rgb(0, 255, 255)" : "rgb(69, 207, 207)";
    const defaultValue = 18;

    // Calculate position of default marker as percentage
    const defaultPosition = ((defaultValue - min) / (max - min)) * 100;

    return (
        <label style={{
            display: 'flex',
            flexDirection: 'row',
            width: '15.3dvw',
            alignItems: 'flex-end',
            textAlign: 'left',
            cursor: 'default',
            gap: '0.5dvw',
            margin: '0.7dvw',
            padding: '0.5dvw',
            outline: '0.04dvw solid hsl(45, 34.00%, 62.50%, 0.5)',
            borderRadius: '0.4dvw',
            backgroundColor: appTheme === 'dark' ? 'rgba(0, 0, 0, 0.7)' :
                             appTheme === 'light' ? 'rgba(124, 124, 124, 0.65)' :
                             'rgba(26, 26, 26, 0.8)',
            WebkitTapHighlightColor: 'transparent'
        }}>
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                fontSize: '0.85dvw',
                color: colorOn,
                fontWeight: '700',
                flex: 1,
                position: 'relative'
            }}>
                <span style={{ whiteSpace: 'nowrap' }}>{label}</span>
                <div style={{ position: 'relative', width: '100%', marginTop: '0.3dvw', paddingTop: '0.2dvw', paddingBottom: '0.2dvw' }}>
                    <input
                        type="range"
                        min={min}
                        max={max}
                        value={value}
                        onChange={(e) => onChange(Number(e.target.value))}
                        style={{
                            width: '100%',
                            height: '0.6dvw',
                            borderRadius: '0.3dvw',
                            outline: 'none',
                            backgroundColor: 'rgba(255, 255, 255, 0.3)',
                            cursor: 'grab',
                            WebkitAppearance: 'none',
                            appearance: 'none',
                            position: 'relative',
                            zIndex: 1
                        }}
                        onMouseDown={(e) => { e.currentTarget.style.cursor = 'ew-resize'; }}
                        onMouseUp={(e) => { e.currentTarget.style.cursor = 'grab'; }}
                        className="settings-slider"
                    />
                    {/* Default marker line at position 18 */}
                    <div style={{
                        position: 'absolute',
                        left: `calc(0.6dvw + (100% - 1.2dvw) * ${defaultPosition} / 100 - 0.075dvw)`,
                        top: '0.2dvw',
                        bottom: '0.2dvw',
                        width: '0.15dvw',
                        backgroundColor: colorOn,
                        opacity: 1.0,
                        pointerEvents: 'none',
                        borderRadius: '0.1dvw',
                        zIndex: 0
                    }} />
                </div>
            </div>
            <span
                onClick={(e) => {
                    e.stopPropagation();
                    onChange(defaultValue);
                }}
                style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    padding: '0.3dvw 0.6dvw',
                    borderRadius: '0.3dvw',
                    minWidth: '2dvw',
                    textAlign: 'center',
                    fontSize: '0.9dvw',
                    color: colorOn,
                    fontWeight: '700',
                    cursor: 'pointer',
                    alignSelf: 'flex-end'
                }}
            >{value}</span>
        </label>
    );
};

// Add more settings elements here as needed